/**
 * Groups each product card's blocks into three containers — media, details, actions — so
 * the list view can lay them out as a simple flex row.
 *
 * This has to happen in JavaScript. The card renders its blocks with
 * `{% content_for 'blocks' %}` (blocks/_product-card.liquid), which emits every block as
 * one HTML string with no way to split it in Liquid, so there is nothing to wrap at
 * render time. Reserving space with padding instead was what caused the sideways scroll.
 *
 * Grouping is reversed when the shopper switches back to a grid view, and re-applied to
 * cards that arrive from filtering or pagination.
 */

const GROUPED = 'data-list-grouped';

const ROLES = [
  ['product-card__list-media', '.card-gallery'],
  [
    'product-card__list-actions',
    '.product-card__login-for-price, product-price, .buy-buttons-block, .product-card-purchase',
  ],
];

/** Guards against reacting to our own DOM changes. */
let busy = false;

/**
 * @param {Element} el
 * @param {string} selector
 */
const holds = (el, selector) => el.matches(selector) || el.querySelector(selector) != null;

/** @param {Element} card */
function group(card) {
  if (card.hasAttribute(GROUPED)) return;

  const media = document.createElement('div');
  media.className = 'product-card__list-media';
  const details = document.createElement('div');
  details.className = 'product-card__list-details';
  const actions = document.createElement('div');
  actions.className = 'product-card__list-actions';

  for (const child of [...card.children]) {
    if (holds(child, ROLES[0][1])) media.appendChild(child);
    else if (holds(child, ROLES[1][1])) actions.appendChild(child);
    else details.appendChild(child);
  }

  // The stock pill rides along inside the purchase panel, but it belongs with the text —
  // under the part number, as it is in the grid — so it is put back into the details column.
  const stock = actions.querySelector('.product-card-purchase__stock');
  if (stock) details.appendChild(stock);

  card.append(media, details, actions);
  card.setAttribute(GROUPED, '');
}

/** @param {Element} card */
function ungroup(card) {
  if (!card.hasAttribute(GROUPED)) return;

  for (const name of ['product-card__list-media', 'product-card__list-details', 'product-card__list-actions']) {
    const wrapper = card.querySelector(`:scope > .${name}`);
    if (!wrapper) continue;
    card.append(...wrapper.childNodes);
    wrapper.remove();
  }

  card.removeAttribute(GROUPED);
}

/** @param {Element} grid */
function sync(grid) {
  const wanted = grid.getAttribute('product-grid-view') === 'list';
  const cards = grid.querySelectorAll('.product-card__content');
  if (!cards.length) return;

  busy = true;
  for (const card of cards) (wanted ? group : ungroup)(card);
  busy = false;
}

/** @param {Element} grid */
function watch(grid) {
  if (grid.hasAttribute('data-list-view-watched')) return;
  grid.setAttribute('data-list-view-watched', '');

  sync(grid);

  // Attribute changes cover the layout toggle; childList covers cards replaced by
  // filtering or pagination, which arrive from the server ungrouped.
  new MutationObserver(() => {
    if (busy) return;
    sync(grid);
  }).observe(grid, {
    attributes: true,
    attributeFilter: ['product-grid-view'],
    childList: true,
    subtree: true,
  });
}

function init() {
  for (const grid of document.querySelectorAll('[product-grid-view]')) watch(grid);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Grids can also appear later, when a section is re-rendered wholesale.
new MutationObserver(() => {
  if (busy) return;
  init();
}).observe(document.documentElement, { childList: true, subtree: true });
